import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import ActionsContext from "common/ActionsContext";
import DataTable, { Column } from "common/DataTable";
import Request from "common/Request";
import Modal from "common/Modal.jsx";
import LoadingIcon from "common/Loading";

const doesNothing = () => {};

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const TableContainer = styled.div`
  width: 100%;
  overflow: auto;
`;

const BootModal = () => {
  const [open, setOpen] = useState(false);
  const [params, setParams] = useState({});
  const actions = useContext(ActionsContext);
  const [errorFooter, setErrorFooter] = useState("");
  const { name, version } = params;
  const [loading, setLoading] = useState(false);

  const apply = () => {
    setLoading(true);
    return ifCl
      .run(`system software boot on ${name} ${version}`)
      .then((response) => {
        setLoading(false);
        if (response) {
          console.log("software boot .<%s>.error: <%s>", response);
          throw `Warning: ${response}`;
        } else {
          actions.send("do-load");
          setOpen(false);
        }
      })
      .catch((error) => {
        setLoading(false);
        setErrorFooter(error);
      });
  };

  useEffect(
    () =>
      actions.recv(`boot-software`, ({ name, version }) => {
        setParams({ name, version });
        setErrorFooter("");
        setOpen(true);
        setLoading(false);
      }),
    []
  );

  if (!open) {
    return null;
  }
  return (
    <Modal
      title={`Set boot flag to ${name} - ${version}?`}
      superIcon="warning"
      onClose={() => setOpen(false)}
      className="modal-software-install"
      closeLabel="CANCEL"
      applyLabel="OK"
      onApply={() => apply()}
      content={() => (
        <>
          <div>
          This package will be the one loaded after reboot.
          </div>
          {loading ? <LoadingWrapper>
            <LoadingIcon />
          </LoadingWrapper> : null}
        </>
      )}
      footerMessage={
        errorFooter !== "" ? (
          <span className="modal-err-msg color-red">
            <h5>{errorFooter}</h5>
          </span>
        ) : null
      }
    />
  );
};

const ReloadModal = () => {
  const [open, setOpen] = useState(false);
  const [params, setParams] = useState({});
  const actions = useContext(ActionsContext);
  const [errorFooter, setErrorFooter] = useState("");
  const { name, version } = params;
  const [loading, setLoading] = useState(false);

  const apply = () => {
    setLoading(true);
    return ifCl
      .run(`system software reload ${name} ${version} force`)
      .then((response) => {
        if (response) {
          console.log("software reload .<%s>.error: <%s>", response);
          throw `Warning: ${response}`;
        }
      })
      .catch((error) => {
        setLoading(false);
        setErrorFooter(error);
      });
  };

  useEffect(
    () =>
      actions.recv(`reload-software`, ({ name, version }) => {
        setParams({ name, version });
        setErrorFooter("");
        setOpen(true);
        setLoading(false);
      }),
    []
  );

  if (!open) {
    return null;
  }
  return (
    <Modal
      title={`Reload package ${name}-${version}?`}
      superIcon="cached"
      className="modal-software-install"
      onClose={() => setOpen(false)}
      closeLabel="CANCEL"
      applyLabel={loading ? null : "OK"}
      onApply={loading ? doesNothing : () => apply()}
      content={() => (
        <>
          {!loading ? <div>
            <b>WARNING</b>: This will interrupt traffic for 10-20 seconds
            and it will be necessary to reload the page and login again.
          </div> : null}
          {loading ? <div>
            <b>Activating</b>: Wait a few seconds and refresh the browser.
          </div> : null}
        </>
      )}
      footerMessage={
        errorFooter !== "" ? (
          <span className="modal-err-msg color-red">
            <h5>{errorFooter}</h5>
          </span>
        ) : null
      }
    />
  );
};

const DeleteModal = () => {
  const [open, setOpen] = useState(false);
  const [params, setParams] = useState({});
  const actions = useContext(ActionsContext);
  const [errorFooter, setErrorFooter] = useState("");

  const apply = () => {
    return (
      ifCl
        .run(`system software remove ${name} ${version}`)
        .then((response) => {
          if (response) {
            console.log("software delete .<%s>.error: <%s>", response);
            throw `Warning: ${response}`;
          } else {
            actions.send("do-load");
            setOpen(false);
          }
        })
        .catch((error) => {
          setErrorFooter(error);
        })
    );
  };

  useEffect(
    () =>
      actions.recv(`delete-software`, ({ name, version }) => {
        setParams({ name, version });
        setErrorFooter("");
        setOpen(true);
      }),
    []
  );
  const { name, version } = params;
  if (!open) {
    return null;
  }
  return (
    <Modal
      title={`Delete package ${name} - ${version}?`}
      superIcon="delete"
      className="modal-software-install"
      onClose={() => setOpen(false)}
      closeLabel="CANCEL"
      applyLabel="OK"
      onApply={() => apply()}
      footerMessage={
        errorFooter !== "" ? (
          <span className="modal-err-msg color-red">
            <h5>{errorFooter}</h5>
          </span>
        ) : null
      }
    />
  );
};

const parseYesOrNo = (input) =>
  input === "yes" ? true : input === "no" ? false : undefined;

const TickColumn = ({ ...settings }) =>
  Column.Value(
    (value) =>
      value === undefined
        ? ""
        : `<i class="material-icons">${
            value ? "check_box" : "check_box_outline_blank"
          }</i>`,
    {
      parse: parseYesOrNo,
      ...settings,
    }
  );

const HiddenYesOrNo = ({ ...settings }) => ({
  ...Column.Hidden(settings),
  parse: parseYesOrNo,
});

const List = ({ response }) => {
  const actions = useContext(ActionsContext);
  const softwareActions = (
    [_name, _version, isActive, isBoot, isReload]
  ) => [
    ...(!isReload
      ? []
      : [
          {
            id: "send-reload-software",
            label: "Reload",
            icon: "cached",
            onClick: (_value, [name, version]) => {
              actions.send("reload-software", { name, version });
            },
          },
        ]),
    ...(isBoot || isActive
      ? []
      : [
          {
            id: "send-delete-software",
            label: "Delete",
            icon: "delete",
            onClick: (_value, [name, version]) => {
              actions.send("delete-software", { name, version });
            },
          },
        ]),
  ];

  return (
    <>
      <TableContainer>
        <DataTable
          pageLength={50}
          exportAsCSV={true}
          content={response}
          columns={[
            Column.Text({
              label: "NAME",
              idx: 0,
            }),
            Column.Text({
              label: "VERSION",
              idx: 1,
            }),
            TickColumn({
              label: "ACTIVE",
              idx: 2,
            }),
            TickColumn({
              label: "BOOT",
              idx: 3,
              cellClassName: (value) => (value === false ? "clickable" : ""),
              onClick: (booted, [name, version]) => {
                booted === false && actions.send("boot-software", { name, version })
              },
            }),
            HiddenYesOrNo({
              label: "RELOAD",
              idx: 4,
            }),
            Column.Actions({
              label: "ACTIONS",
              idx: 0,
              are: (source, row) => softwareActions(row),
              colClassName: "text-center action",
              cellClassName: "hides-content-to-operators",
            }),
          ]}
        />
      </TableContainer>
      <DeleteModal />
      <ReloadModal />
      <BootModal />
    </>
  );
};

const SoftwareList = () => {
  const [request, setRequest] = useState(null);
  const actions = useContext(ActionsContext);

  const doRequest = () =>
    ifCl.run("show system software").then((response) => ({ response }));

  const doLoad = () => {
    setRequest(doRequest());
  };

  useEffect(() => (doLoad(), actions.recv("do-load", doLoad)), []);

  if (request === null) return null;

  return <Request during={request}>{(result) => <List {...result} />}</Request>;
};

export default SoftwareList;
